import { useState } from 'react'
import { AuthService } from '../../services/auth'
import { AttachmentService, type VideoAttachment, type AttachmentType } from '../../services/attachments'
import type { Video } from '../../services/videos'
import { MAX_ATTACHMENT_SIZE, formatFileSize, validateAttachment } from '../../utils/attachmentLimits'

interface AttachmentManagementSectionProps {
  video: Video
  attachments: VideoAttachment[]
  onAttachmentsUpdate: () => void
  onError: (error: string) => void
}

const AttachmentManagementSection = ({ video, attachments, onAttachmentsUpdate, onError }: AttachmentManagementSectionProps) => {
  const [uploading, setUploading] = useState<AttachmentType | null>(null)
  const [courseMaterialFile, setCourseMaterialFile] = useState<File | null>(null)
  const [worksheetFile, setWorksheetFile] = useState<File | null>(null)

  const handleFileUpload = async (type: AttachmentType, file: File) => {
    try {
      setUploading(type)
      onError('')

      const user = await AuthService.getCurrentUser()
      if (!user) {
        onError('ユーザー認証が必要です')
        return
      }

      await AttachmentService.uploadAttachment(video.id, file, type, user.id)

      if (type === 'course_material') {
        setCourseMaterialFile(null)
      } else {
        setWorksheetFile(null)
      }

      const fileInput = document.getElementById(`${type}-${video.id}`) as HTMLInputElement
      if (fileInput) fileInput.value = ''

      onAttachmentsUpdate()
    } catch (error: unknown) {
      console.error('Failed to upload attachment:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      onError(`ファイルのアップロードに失敗しました: ${errorMessage}`)
    } finally {
      setUploading(null)
    }
  }

  const handleFileDelete = async (attachmentId: string) => {
    if (!confirm('このファイルを削除してもよろしいですか？')) return

    try {
      await AttachmentService.deleteAttachment(attachmentId)
      onAttachmentsUpdate()
    } catch (error: unknown) {
      console.error('Failed to delete attachment:', error)
      const errorMessage = error instanceof Error ? error.message : String(error)
      onError(`ファイルの削除に失敗しました: ${errorMessage}`)
    }
  }

  const courseMaterials = attachments.filter(a => a.attachment_type === 'course_material')
  const worksheets = attachments.filter(a => a.attachment_type === 'worksheet')

  return (
    <div className="space-y-4">
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          📎 添付ファイル管理
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Course Materials */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">
              講座資料 <span className="font-normal text-gray-500">（最大 {formatFileSize(MAX_ATTACHMENT_SIZE)}）</span>
            </h5>
            <div className="mb-3">
              <input
                id={`course_material-${video.id}`}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  // Say why up front: an over-sized file only fails after the whole
                  // upload, with a bare 413 from the Storage API.
                  const validationError = validateAttachment(file)
                  if (validationError) {
                    onError(`${file.name}: ${validationError}`)
                    e.target.value = ''
                    setCourseMaterialFile(null)
                    return
                  }
                  onError('')
                  setCourseMaterialFile(file)
                }}
                className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
              />
              {courseMaterialFile && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    {courseMaterialFile.name} ({AttachmentService.formatFileSize(courseMaterialFile.size)})
                  </span>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleFileUpload('course_material', courseMaterialFile)}
                      disabled={uploading === 'course_material'}
                      className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-2 py-1 rounded text-xs"
                    >
                      {uploading === 'course_material' ? 'アップロード中...' : 'アップロード'}
                    </button>
                    <button
                      onClick={() => setCourseMaterialFile(null)}
                      className="text-gray-500 hover:text-gray-700 text-xs"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {courseMaterials.length > 0 ? (
                courseMaterials.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{AttachmentService.getFileIcon(attachment.mime_type)}</span>
                      <div>
                        <p className="text-sm text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {AttachmentService.formatFileSize(attachment.file_size)} •{' '}
                          {new Date(attachment.created_at).toLocaleDateString('ja-JP')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleFileDelete(attachment.id)}
                      className="text-red-600 hover:text-red-500 text-xs font-medium"
                    >
                      削除
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">講座資料がありません</p>
              )}
            </div>
          </div>

          {/* Worksheets */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">
              ワークシート <span className="font-normal text-gray-500">（最大 {formatFileSize(MAX_ATTACHMENT_SIZE)}）</span>
            </h5>
            <div className="mb-3">
              <input
                id={`worksheet-${video.id}`}
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt"
                onChange={(e) => {
                  const file = e.target.files?.[0]
                  if (!file) return
                  const validationError = validateAttachment(file)
                  if (validationError) {
                    onError(`${file.name}: ${validationError}`)
                    e.target.value = ''
                    setWorksheetFile(null)
                    return
                  }
                  onError('')
                  setWorksheetFile(file)
                }}
                className="text-xs border border-gray-300 rounded px-2 py-1 w-full"
              />
              {worksheetFile && (
                <div className="mt-2 flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    {worksheetFile.name} ({AttachmentService.formatFileSize(worksheetFile.size)})
                  </span>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleFileUpload('worksheet', worksheetFile)}
                      disabled={uploading === 'worksheet'}
                      className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-2 py-1 rounded text-xs"
                    >
                      {uploading === 'worksheet' ? 'アップロード中...' : 'アップロード'}
                    </button>
                    <button
                      onClick={() => setWorksheetFile(null)}
                      className="text-gray-500 hover:text-gray-700 text-xs"
                    >
                      キャンセル
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {worksheets.length > 0 ? (
                worksheets.map((attachment) => (
                  <div key={attachment.id} className="flex items-center justify-between p-2 bg-white rounded border">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{AttachmentService.getFileIcon(attachment.mime_type)}</span>
                      <div>
                        <p className="text-sm text-gray-900">{attachment.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {AttachmentService.formatFileSize(attachment.file_size)} •{' '}
                          {new Date(attachment.created_at).toLocaleDateString('ja-JP')}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleFileDelete(attachment.id)}
                      className="text-red-600 hover:text-red-500 text-xs font-medium"
                    >
                      削除
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500">ワークシートがありません</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-blue-700">
            📝 対応ファイル形式: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, PNG, JPG, JPEG, GIF, TXT（最大50MB）
          </p>
          <p className="text-xs text-blue-600 mt-1">
            添付ファイルは動画プレーヤーページで生徒がダウンロードできます。
          </p>
        </div>
      </div>
    </div>
  )
}

export default AttachmentManagementSection
